import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FolderKanban, Plus, Search, ChevronRight, ArrowLeft, Send, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/lib/database.types";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Spinner, EmptyState,
} from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import AssignPersonalBookingModal from "@/features/schedule/AssignPersonalBookingModal";

// =============================================================================
// Projects list
// =============================================================================
// Reads projects + joined client/site/carrier/PM.
// Director / manager see all fields incl. contract value; supervisor / field /
// subcontractor see no financial column.
// "Active" filter covers scheduled + mobilising + live + acceptance_pending.
// =============================================================================

const STATUS_FILTERS = [
  { v: "active",             label: "Active" },
  { v: "scheduled",          label: "Scheduled" },
  { v: "live",               label: "Live" },
  { v: "mobilising",         label: "Mobilising" },
  { v: "acceptance_pending", label: "Acceptance" },
  { v: "on_hold",            label: "On hold" },
  { v: "closed",             label: "Closed" },
  { v: "all",                label: "All" },
] as const;

// -----------------------------------------------------------------------------
// 4 Nov 2026 — James: "a cool and easy way to filter and see the clients we use
// a lot or have work with, and carriers. Currently I have to text search APD to
// narrow it down and I use this a lot." Two chip rows under the status bar:
// clients, then carriers — each chip is a colour dot, the name and how many
// jobs it has in the current status filter, busiest first. One click filters,
// click again clears. The pick lives in the URL (?client=APD&carrier=TELSTRA)
// so back/forward and a bookmarked "APD live jobs" both work. Counted on the
// client side from the rows already loaded, so the numbers always agree with
// the table.
// -----------------------------------------------------------------------------
function hueFor(s: string): number { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h % 360; }
function Chip({ label, n, active, hue, onClick }: { label: string; n: number; active: boolean; hue: number; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} title={`${n} job${n === 1 ? "" : "s"} in this view`}
      className={`inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-[12px] border transition-colors ${active ? "border-accent bg-accent/12 text-accent font-medium" : "border-line bg-ink-raised text-text-muted hover:text-text"}`}>
      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: `hsl(${hue} 70% 55%)` }}/>
      <span className="truncate max-w-[160px]">{label}</span>
      <span className={`font-mono text-[10.5px] px-1 rounded ${active ? "bg-accent/20" : "bg-ink text-text-dim"}`}>{n}</span>
      {active && <X size={11}/>}
    </button>
  );
}

export default function ProjectsListScreen() {
  const user = useRequireAuth();
  const [status, setStatus] = useState<string>("active");
  const [search, setSearch] = useState("");
  const [sp, setSp] = useSearchParams();
  const clientPick = sp.get("client") ?? "";
  const carrierPick = sp.get("carrier") ?? "";
  const setPick = (k: "client" | "carrier", v: string) => { const p = new URLSearchParams(sp); if (v && p.get(k) !== v) p.set(k, v); else p.delete(k); setSp(p, { replace: true }); };
  const [assignTaskOpen, setAssignTaskOpen] = useState(false); // T-249c

  const canSeeFinancials = hasRole(user, "manager");

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects_list", status, search],
    queryFn: async () => {
      let q = supabase
        .from("projects")
        .select(`
          project_id, project_code, project_name, status, contract_value,
          scheduled_start_date, scheduled_end_date, risk_rating,
          clients(legal_name, client_code),
          carriers(carrier_code),
          sites(site_code, site_name, suburb),
          pm:employees!projects_project_manager_id_fkey(first_name, last_name)
        `)
        .order("scheduled_start_date", { ascending: false, nullsFirst: false })
        .limit(200);

      if (status === "active") {
        q = q.in("status", ["scheduled", "mobilising", "live", "acceptance_pending"]);
      } else if (status !== "all") {
        // T-162a — STATUS_FILTERS includes "active" and "all" which aren't enum values;
        // when we reach .eq() we know status is a real project_status. Narrow for TS.
        q = q.eq("status", status as Database["public"]["Enums"]["project_status"]);
      }

      if (search.trim()) {
        q = q.or(`project_code.ilike.%${search}%,project_name.ilike.%${search}%`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });

  // chips: counted from the rows in this status filter + search
  const clientChips = useMemo(() => {
    const m = new Map<string, { key: string; label: string; n: number }>();
    for (const p of projects) { const key = p.clients?.client_code ?? p.clients?.legal_name ?? ""; if (!key) continue; const c = m.get(key) ?? { key, label: p.clients?.legal_name ?? key, n: 0 }; c.n++; m.set(key, c); }
    return [...m.values()].sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
  }, [projects]);
  const carrierChips = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of projects) { const k = p.carriers?.carrier_code ?? "__none"; m.set(k, (m.get(k) ?? 0) + 1); }
    // mig 507 — "no carrier" is a chip too, so the ones still to set are one click away
    return [...m.entries()].map(([key, n]) => ({ key, label: key === "__none" ? "no carrier set" : key, n })).sort((a, b) => (a.key === "__none" ? 1 : b.key === "__none" ? -1 : 0) || b.n - a.n || a.label.localeCompare(b.label));
  }, [projects]);
  const shown = projects.filter(p =>
    (!clientPick || (p.clients?.client_code ?? p.clients?.legal_name) === clientPick) &&
    (!carrierPick || (carrierPick === "__none" ? !p.carriers?.carrier_code : p.carriers?.carrier_code === carrierPick)));

  const headers = [
    "Project", "Client", "Site", "Status",
    ...(canSeeFinancials ? ["Value"] : []),
    "Start", "PM", "",
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      {/* Back to Project portal */}
      <Link
        to="/project"
        className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"
      >
        <ArrowLeft size={14}/> Project portal
      </Link>

      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Every job, past and present.
          </div>
        </div>
        {hasRole(user, "manager") && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="secondary" icon={Send} onClick={() => setAssignTaskOpen(true)}>
              Assign task
            </Button>
            <Link to="/projects/new"><Button icon={Plus}>New project</Button></Link>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md w-full sm:w-80">
          <Search size={14} className="text-text-muted" />
          <input
            placeholder="Search code or name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent border-none outline-none text-text text-[13px] flex-1"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUS_FILTERS.map(({ v, label }) => (
            <button
              key={v}
              onClick={() => setStatus(v)}
              className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 font-semibold border transition-colors ${
                status === v
                  ? "bg-accent/12 text-accent border-accent/30"
                  : "bg-ink-raised text-text-muted border-line hover:text-text"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Client + carrier chips */}
      {!isLoading && (clientChips.length > 1 || carrierChips.length > 1 || clientPick || carrierPick) && (
        <div className="mb-4 space-y-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted w-14">Client</span>
            {clientChips.map(c => <Chip key={c.key} label={c.label} n={c.n} hue={hueFor(c.key)} active={clientPick === c.key} onClick={() => setPick("client", c.key)}/>)}
          </div>
          {carrierChips.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted w-14">Carrier</span>
              {carrierChips.map(c => <Chip key={c.key} label={c.label} n={c.n} hue={c.key === "__none" ? 40 : hueFor("carrier:" + c.key)} active={carrierPick === c.key} onClick={() => setPick("carrier", c.key)}/>)}
            </div>
          )}
          {(clientPick || carrierPick) && (
            <div className="text-[12px] text-text-muted">
              Showing <b className="text-text">{shown.length}</b> of {projects.length}{clientPick ? <> · client <b className="text-text">{clientChips.find(c => c.key === clientPick)?.label ?? clientPick}</b></> : null}{carrierPick ? <> · carrier <b className="text-text">{carrierPick === "__none" ? "not set" : carrierPick}</b></> : null}
              <button type="button" className="ml-2 text-accent hover:underline" onClick={() => { const p = new URLSearchParams(sp); p.delete("client"); p.delete("carrier"); setSp(p, { replace: true }); }}>clear</button>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : shown.length === 0 ? (
        <Card>
          <EmptyState
            title="No projects match"
            description="Try broadening the filter or clearing the search."
            icon={FolderKanban}
          />
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line">
                  {headers.map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-medium tracking-wider2 uppercase text-text-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((p, i) => (
                  <tr
                    key={p.project_id}
                    className={`border-b border-line cursor-pointer hover:bg-ink-hover ${i % 2 ? "bg-ink/40" : ""}`}
                    onClick={() => window.location.href = `/projects/${p.project_id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-mono text-[11px] text-accent">{p.project_code}</div>
                      <div className="text-text truncate max-w-[280px]">{p.project_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-text flex items-center gap-1.5">{p.clients && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: `hsl(${hueFor(p.clients.client_code ?? p.clients.legal_name)} 70% 55%)` }}/>}{p.clients?.legal_name ?? "—"}</div>
                      {p.carriers?.carrier_code && (
                        <div className="text-[11px] font-mono text-text-muted">{p.carriers.carrier_code}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {p.sites ? (
                        <>
                          <div className="font-mono text-[11px] text-text-muted">{p.sites.site_code}</div>
                          <div className="text-[11px] text-text-muted truncate max-w-[180px]">{p.sites.suburb}</div>
                        </>
                      ) : <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Pill label={p.status.replace(/_/g, " ")} tone={toneFor(p.status)} small />
                    </td>
                    {canSeeFinancials && (
                      <td className="px-4 py-3 font-mono text-text">{fmtMoney(p.contract_value)}</td>
                    )}
                    <td className="px-4 py-3 font-mono text-[12px] text-text-muted">{fmtDate(p.scheduled_start_date)}</td>
                    <td className="px-4 py-3 text-[12px]">
                      {p.pm ? `${p.pm.first_name} ${p.pm.last_name}` : <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-4 py-3"><ChevronRight size={14} className="text-text-dim" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* T-249c — quick-assign one-off task to an employee */}
      {assignTaskOpen && (
        <AssignPersonalBookingModal
          onClose={() => setAssignTaskOpen(false)}
          onCreated={() => setAssignTaskOpen(false)}
        />
      )}
    </div>
  );
}

function toneFor(status: string): any {
  switch (status) {
    case "scheduled":          return "scheduled";
    case "live":               return "live";
    case "mobilising":         return "mobilising";
    case "acceptance_pending": return "acceptance_pending";
    case "on_hold":            return "on_hold";
    case "closed":             return "closed";
    case "cancelled":          return "cancelled";
    case "won":                return "won";
    case "quoted":             return "quoted";
    default:                   return "default";
  }
}
